import { useEffect, useState } from "react";
import { Users, ShieldCheck, Mail, X, Eye, Banknote, ListChecks, Map as MapIcon } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const PERMS = [
  ["view_group_balance", "View balance", Eye],
  ["view_group_contributions", "View contributions", Banknote],
  ["view_group_payouts", "View payouts", Banknote],
  ["manage_group_tasks", "Manage tasks", ListChecks],
  ["manage_group_members", "Manage members", Users],
  ["manage_group_roadmap", "Manage roadmap", MapIcon],
];
const PERM_LABEL = Object.fromEntries(PERMS.map(([k, l]) => [k, l]));

export function CoOwnerInvites({ onAccepted }) {
  const [invites, setInvites] = useState([]);
  const load = () => api.get("/groups/co-owners/invites").then(({ data }) => setInvites(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!invites.length) return null;
  const accept = async (id) => {
    try {
      await api.post("/groups/co-owners/accept", { invite_id: id });
      toast.success("Co-Owner Access Enabled ✅");
      load(); onAccepted?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not accept"); }
  };
  return (
    <div className="mt-4 space-y-2" data-testid="coowner-invites">
      {invites.map((inv) => (
        <div key={inv.invite_id} className="rounded-xl border border-accent/40 bg-accent/5 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-accent" />
              You've been invited to co-own <strong>{inv.group_name}</strong>
            </div>
            <Button size="sm" onClick={() => accept(inv.invite_id)} data-testid={`accept-invite-${inv.invite_id}`}>Accept co-ownership</Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {(inv.permissions || []).map((p) => (
              <span key={p} className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">{PERM_LABEL[p] || p}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function AjoCoOwnerModule({ groupId, isOwner, group, contributions = [] }) {
  const [coOwners, setCoOwners] = useState([]);
  const [pending, setPending] = useState([]);
  const [email, setEmail] = useState("");
  const [perms, setPerms] = useState(["view_group_balance", "view_group_contributions", "view_group_payouts"]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/groups/${groupId}/co-owners`).then(({ data }) => setCoOwners(data || [])).catch(() => {});
    if (isOwner) api.get(`/groups/${groupId}/co-owners/invites`).then(({ data }) => setPending(data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, [groupId, isOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  const invite = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    if (!perms.length) { toast.error("Pick at least one permission"); return; }
    setBusy(true);
    try {
      await api.post(`/groups/${groupId}/co-owners/invite`, { email, permissions: perms });
      toast.success(`Co-owner invite sent to ${email}`);
      setEmail(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Invite failed"); }
    finally { setBusy(false); }
  };

  const remove = async (uid) => {
    try { await api.delete(`/groups/${groupId}/co-owners/${uid}`); toast.success("Co-owner removed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const cancelInvite = async (id) => {
    try { await api.delete(`/groups/${groupId}/co-owners/invites/${id}`); toast.success("Invite cancelled"); load(); }
    catch { toast.error("Failed"); }
  };

  const totalContrib = contributions.reduce((s, c) => s + Number(c.amount || 0), 0);

  return (
    <div className="mt-6 rounded-2xl border border-primary/30 bg-card p-6" data-testid="ajo-coowner-module">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg">Group Co-Ownership</h2>
        </div>
        <span className="rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[10px] font-medium text-primary">Scoped to this Ajo group only</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">Co-owners share this group's dashboard — never your main Zuntria account, wallet or other groups.</p>

      {/* Shared group dashboard — what co-owners see */}
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="coowner-shared-dashboard">
        <SharedStat label="Group balance" value={`£${totalContrib.toLocaleString()}`} />
        <SharedStat label="Contributions" value={contributions.length} />
        <SharedStat label="Members" value={(group?.members || []).length} />
        <SharedStat label="Payout / cycle" value={`£${Number((group?.amount || 0) * (group?.members?.length || 1)).toLocaleString()}`} />
      </div>

      {/* Active co-owners with their permissions */}
      {coOwners.length > 0 ? (
        <div className="mt-5 space-y-2" data-testid="coowner-list">
          {coOwners.map((c) => (
            <div key={c.user_id} className="rounded-xl border border-border bg-background p-3" data-testid={`coowner-${c.user_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm">
                  {c.name} <span className="text-xs text-muted-foreground">({c.email})</span>
                  <span className="ml-2 text-[10px] text-muted-foreground">since {c.accepted_at?.slice(0, 10)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-500">
                    <ShieldCheck className="h-3 w-3" /> Co-Owner Access Enabled
                  </span>
                  {isOwner && (
                    <button onClick={() => remove(c.user_id)} className="rounded-full border border-border p-1 text-muted-foreground hover:border-red-500/50 hover:text-red-400" data-testid={`remove-coowner-${c.user_id}`}>
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(c.permissions || []).map((p) => (
                  <span key={p} className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">{PERM_LABEL[p] || p}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-4 text-xs text-muted-foreground">No co-owners yet{isOwner ? " — invite someone you trust below." : "."}</p>
      )}

      {/* Pending invites (owner) */}
      {isOwner && pending.length > 0 && (
        <div className="mt-4" data-testid="coowner-pending">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Pending invites</p>
          <div className="mt-2 space-y-1.5">
            {pending.map((inv) => (
              <div key={inv.invite_id} className="flex items-center justify-between rounded-lg border border-dashed border-border bg-background px-3 py-2 text-xs">
                <span>{inv.email} · sent {inv.created_at?.slice(0, 10)}</span>
                <button onClick={() => cancelInvite(inv.invite_id)} className="text-muted-foreground hover:text-red-400" data-testid={`cancel-invite-${inv.invite_id}`}>Cancel</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite flow (owner) */}
      {isOwner && (
        <div className="mt-5 rounded-xl border border-border bg-background p-4">
          <p className="text-xs font-medium">Invite a co-owner</p>
          <div className="mt-2 flex gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="co-owner@email.com" className="h-10" data-testid="coowner-email-input" />
            <Button onClick={invite} disabled={busy} className="h-10" data-testid="coowner-invite-btn">{busy ? "Sending…" : "Invite"}</Button>
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-wider text-muted-foreground">Permissions to grant</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {PERMS.map(([k, label, Icon]) => (
              <button key={k}
                onClick={() => setPerms((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k])}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] ${perms.includes(k) ? "border-primary/60 bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}
                data-testid={`perm-${k}`}>
                <Icon className="h-2.5 w-2.5" /> {label}
              </button>
            ))}
          </div>
          <p className="mt-3 text-[10px] text-muted-foreground">They'll get an in-app invite → once accepted, they see this group's dashboard with exactly the permissions you chose.</p>
        </div>
      )}
    </div>
  );
}

function SharedStat({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3 text-center">
      <div className="font-display text-lg">{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}
